/**
 * Shared sortable / paginated / column-pickable table.
 *
 * Built for issue #5 to give the Worklist, Studies, Reports and
 * Reading pages the same chrome: page-size picker, sortable
 * column headers, and a per-user column-visibility dropdown. The
 * per-table preferences (visible columns, page size, sort order)
 * persist server-side via /viewer-api/user/prefs under the key
 * `table.<tableId>`.
 *
 * Rewritten 2026-05-20 — the original onMount-pump pattern latched
 * on the empty stored() value before the prefs query resolved,
 * which both produced "only the alwaysVisible columns are showing"
 * on first paint AND made server-side persistence look broken
 * because the seeded state ignored later-arrived prefs.  This
 * revision derives column visibility / page size / sort directly
 * from the prefs query via createMemo, with optimistic updates on
 * user changes (queryClient.setQueryData + debounced PUT), so the
 * three states (defaults / loaded prefs / user-pending change)
 * compose reactively without an imperative seed step.
 *
 * Solid-reactivity contract for callers:
 *   - `props.rows` MUST be the result of a `createMemo` or a
 *     reactive accessor; passing a raw array snapshots at mount
 *     and the table never updates.
 *   - `props.columns` is treated as a static spec — pass the
 *     same array on every render (or stash it in a top-level
 *     const, since it's not reactive state).
 */
import {
  createMemo, createSignal, For, Show,
} from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import * as prefs from "../lib/prefs";
import type { JSX } from "solid-js";

export interface DataColumn<T> {
  /** Stable column key — used as the prefs identifier so renaming
   *  the label without renaming the id keeps user preferences. */
  id:               string;
  /** Header label. */
  label:            string;
  /** Cell renderer. */
  render:           (row: T) => JSX.Element;
  /** Returns the value used for sorting. If undefined, the column
   *  is treated as non-sortable. Strings sort case-insensitive;
   *  numbers / Date are compared natively; null/undefined sort last. */
  sortValue?:       (row: T) => string | number | Date | null | undefined;
  /** Initial visibility before the user has set a preference.
   *  Default: true. */
  defaultVisible?:  boolean;
  /** Column the user cannot hide via the column picker — useful
   *  for the row's primary identifier (e.g. Patient on Worklist). */
  alwaysVisible?:   boolean;
  /** Tailwind class for column width / alignment overrides. */
  cellClass?:       string;
  headerClass?:     string;
}

export interface DataTableProps<T> {
  /** Stable string for the prefs key. Two tables with the same
   *  tableId share preferences — keep them distinct. */
  tableId:          string;
  /** Reactive accessor returning the current row set. */
  rows:             () => T[];
  /** Static spec — see file-header note. */
  columns:          DataColumn<T>[];
  /** Stable per-row key for keyed rendering. */
  rowKey:           (row: T) => string;
  onRowClick?:      (row: T) => void;
  /** Default sort applied before the user has stored a preference. */
  defaultSort?:     { col: string; dir: "asc" | "desc" };
  /** Default page size before the user has stored a preference. */
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  emptyState?:      JSX.Element;
  /** Optional toolbar slot rendered above the table (e.g. status
   *  tabs, search box). */
  toolbar?:         JSX.Element;
  /** Loading sentinel for when `rows()` is still resolving. */
  loading?:         () => boolean;
}

interface TablePrefs {
  columns?:  string[];                                // visible column ids, in order
  pageSize?: number;
  sort?:     { col: string; dir: "asc" | "desc" };
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE         = 50;
const PREFS_QUERY_KEY           = ["user-prefs"] as const;

export function DataTable<T>(props: DataTableProps<T>) {
  const qc = useQueryClient();
  const prefsKey = createMemo(() => `table.${props.tableId}`);

  // ---- prefs: load once on mount, then optimistic-update on changes ----
  const prefsQuery = createQuery(() => ({
    queryKey: PREFS_QUERY_KEY,
    queryFn:  () => prefs.getAll(),
    staleTime: 60_000,
  }));

  /** Server-stored slot for this table — empty object until the
   *  prefs query resolves, then the user's saved state. */
  const serverSlot = createMemo<TablePrefs>(() => {
    const all = prefsQuery.data ?? {};
    const slot = all[prefsKey()];
    return (slot && typeof slot === "object") ? (slot as TablePrefs) : {};
  });

  /** Local override — null until the user changes something in THIS
   *  session.  Once set it wins over the server slot.  This is the
   *  reactivity fix: a plain Solid signal is synchronously reactive,
   *  whereas the previous reliance on queryClient.setQueryData to
   *  re-drive the memos was racy (the checkbox snapped straight back
   *  because the derived state didn't recompute before the native
   *  control re-rendered). */
  const [localSlot, setLocalSlot] = createSignal<TablePrefs | null>(null);

  /** Effective prefs = local override if the user has touched this
   *  table this session, else whatever the server returned. Purely
   *  derived — no imperative seed step, so there is no first-paint
   *  race: before the query resolves it is {} (defaults shown), and
   *  the instant the saved prefs land the memo flips to them. */
  const effective = createMemo<TablePrefs>(() => localSlot() ?? serverSlot());

  /** Live (rendered) column-id list. */
  const visibleIds = createMemo<string[]>(() => {
    const cols = effective().columns;
    if (Array.isArray(cols)) {
      const live = new Set(props.columns.map((c) => c.id));
      const ids = cols.filter((id) => live.has(id));
      // Re-add alwaysVisible columns the stored set may have dropped.
      for (const c of props.columns) {
        if (c.alwaysVisible && !ids.includes(c.id)) ids.push(c.id);
      }
      return ids;
    }
    // No stored prefs — render the column spec's default-visible set.
    return props.columns
      .filter((c) => c.alwaysVisible || c.defaultVisible !== false)
      .map((c) => c.id);
  });

  const sortBy = createMemo<TablePrefs["sort"] | null>(
    () => effective().sort ?? props.defaultSort ?? null,
  );

  const pageSize = createMemo<number>(() => {
    const s = effective().pageSize;
    if (typeof s === "number" && s > 0) return s;
    return props.defaultPageSize ?? DEFAULT_PAGE_SIZE;
  });

  // ---- prefs writeback: local signal (sync) + debounced PUT -----------
  let writeTimer: ReturnType<typeof setTimeout> | null = null;
  function patchPrefs(patch: Partial<TablePrefs>) {
    const key = prefsKey();
    // 1. Update the local override synchronously — derived memos
    //    recompute immediately so the checkbox / select / sort
    //    arrow reflects the change on this same tick.
    const next: TablePrefs = { ...effective(), ...patch };
    setLocalSlot(next);
    // 2. Mirror into the query cache too so a remount within the
    //    session (before the GET refetches) sees the new value.
    qc.setQueryData<Record<string, any>>(PREFS_QUERY_KEY as any, (prev) => {
      const all = prev ?? {};
      return { ...all, [key]: next };
    });
    // 3. Debounced server-side persist. Sends only {tableKey: slot}
    //    so the user_prefs row's other top-level keys (e.g.
    //    break_glass.default_reason) are untouched.
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(async () => {
      try {
        const merged = await prefs.put({ [key]: next });
        qc.setQueryData(PREFS_QUERY_KEY as any, merged);
      } catch { /* silent — table chrome shouldn't pop alerts */ }
    }, 400);
  }

  // ---- derived: live columns, sorted/paged rows -----------------------
  const liveColumns = createMemo(() => {
    const ids  = visibleIds();
    const byId = new Map(props.columns.map((c) => [c.id, c]));
    return ids.map((id) => byId.get(id)).filter(Boolean) as DataColumn<T>[];
  });

  const sortedRows = createMemo(() => {
    const all = props.rows();
    const s   = sortBy();
    if (!s) return all;
    const col = props.columns.find((c) => c.id === s.col);
    if (!col?.sortValue) return all;
    const dir = s.dir === "asc" ? 1 : -1;
    return [...all].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;            // nulls last regardless of dir
      if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv) * dir;
      }
      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * dir;
      }
      return ((av as any) < (bv as any) ? -1 : (av as any) > (bv as any) ? 1 : 0) * dir;
    });
  });

  const [page, setPage] = createSignal(1);
  const totalRows  = createMemo(() => sortedRows().length);
  const totalPages = createMemo(() => Math.max(1, Math.ceil(totalRows() / pageSize())));
  // Clamp page when filters or page-size shrink the row count.
  const clampedPage = createMemo(() => Math.min(Math.max(1, page()), totalPages()));
  const pageRows = createMemo(() => {
    const ps = pageSize();
    const start = (clampedPage() - 1) * ps;
    return sortedRows().slice(start, start + ps);
  });

  // ---- handlers --------------------------------------------------------
  function toggleSort(colId: string) {
    const col = props.columns.find((c) => c.id === colId);
    if (!col?.sortValue) return;
    const cur = sortBy();
    const next: TablePrefs["sort"] = cur?.col === colId
      ? { col: colId, dir: cur.dir === "asc" ? "desc" : "asc" }
      : { col: colId, dir: "desc" };   // newest-first bias
    setPage(1);
    patchPrefs({ sort: next });
  }

  function toggleColumn(colId: string) {
    const col = props.columns.find((c) => c.id === colId);
    if (!col || col.alwaysVisible) return;
    const cur = new Set(visibleIds());
    if (cur.has(colId)) cur.delete(colId); else cur.add(colId);
    // Preserve the spec order so columns don't shuffle when toggled
    // off and back on.
    const nextIds = props.columns.map((c) => c.id).filter((id) => cur.has(id));
    patchPrefs({ columns: nextIds });
  }

  function changePageSize(n: number) {
    setPage(1);
    patchPrefs({ pageSize: n });
  }

  const [pickerOpen, setPickerOpen] = createSignal(false);
  const opts = createMemo(() => props.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS);

  // ---- render ----------------------------------------------------------
  return (
    <div class="flex h-full min-h-0 flex-col gap-2">
      {/* Toolbar — caller content (status tabs, search) on the left,
          our column picker on the right. */}
      <div class="flex items-center gap-3">
        <div class="flex-1">{props.toolbar}</div>
        <div class="relative">
          <button
            type="button"
            class="rounded-md border border-ink-300 px-3 py-1.5 text-sm text-ink-900 hover:bg-ink-100"
            aria-haspopup="menu"
            aria-expanded={pickerOpen()}
            onClick={() => setPickerOpen((v) => !v)}
          >Columns ▾</button>
          <Show when={pickerOpen()}>
            <div
              role="menu"
              class="absolute right-0 z-30 mt-1 min-w-[200px] rounded-md border border-ink-300 bg-ink-100 p-1 shadow-lg"
              onMouseLeave={() => setPickerOpen(false)}
            >
              <For each={props.columns}>{(c) => {
                const checked = createMemo(() => visibleIds().includes(c.id));
                return (
                  <label class="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-ink-900 hover:bg-ink-200">
                    <input
                      type="checkbox"
                      class="accent-brand"
                      checked={checked()}
                      disabled={c.alwaysVisible}
                      onChange={() => toggleColumn(c.id)}
                    />
                    <span>{c.label}</span>
                    <Show when={c.alwaysVisible}>
                      <span class="ml-auto text-[10px] uppercase text-ink-600">always</span>
                    </Show>
                  </label>
                );
              }}</For>
            </div>
          </Show>
        </div>
      </div>

      {/* Table — the inner scroll container.  flex-1 + min-h-0 lets it
          fill the available vertical space without ballooning past the
          parent.  Sticky thead stays pinned to the top of THIS scroll
          container as the user scrolls the rows. */}
      <div class="flex-1 min-h-0 overflow-auto rounded-md border border-ink-300">
        <table class="w-full table-auto text-sm">
          <thead class="sticky top-0 z-20 bg-ink-200 text-ink-900 shadow-[0_1px_0_0_var(--tw-shadow-color)] shadow-ink-300">
            <tr>
              <For each={liveColumns()}>{(c) => {
                const s = createMemo(() => sortBy());
                const arrow = createMemo(() => {
                  const cur = s();
                  if (!cur || cur.col !== c.id) return c.sortValue ? "  " : "";
                  return cur.dir === "asc" ? " ▲" : " ▼";
                });
                return (
                  <th
                    class={`whitespace-nowrap px-3 py-2 text-left font-medium ${c.headerClass ?? ""} ${c.sortValue ? "cursor-pointer select-none hover:bg-ink-300" : ""}`}
                    onClick={() => c.sortValue && toggleSort(c.id)}
                  >
                    {c.label}<span class="text-ink-600">{arrow()}</span>
                  </th>
                );
              }}</For>
            </tr>
          </thead>
          <tbody class="divide-y divide-ink-300 bg-ink-100 text-ink-900">
            <Show
              when={!props.loading?.() && pageRows().length > 0}
              fallback={
                <tr>
                  <td colspan={liveColumns().length} class="px-3 py-8 text-center text-ink-600">
                    <Show when={props.loading?.()} fallback={props.emptyState ?? "No rows"}>
                      Loading…
                    </Show>
                  </td>
                </tr>
              }
            >
              <For each={pageRows()}>{(row) => (
                <tr
                  class={`${props.onRowClick ? "cursor-pointer hover:bg-ink-200" : ""}`}
                  onClick={() => props.onRowClick?.(row)}
                >
                  <For each={liveColumns()}>{(c) => (
                    <td class={`whitespace-nowrap px-3 py-2 ${c.cellClass ?? ""}`}>
                      {c.render(row)}
                    </td>
                  )}</For>
                </tr>
              )}</For>
            </Show>
          </tbody>
        </table>
      </div>

      {/* Paginator */}
      <div class="flex items-center justify-between text-sm text-ink-700">
        <div>
          <span>{totalRows()} {totalRows() === 1 ? "row" : "rows"}</span>
          <Show when={totalRows() > 0}>
            <span class="ml-2 text-ink-600">
              · showing {((clampedPage() - 1) * pageSize()) + 1}–
              {Math.min(clampedPage() * pageSize(), totalRows())}
            </span>
          </Show>
        </div>
        <div class="flex items-center gap-3">
          <label class="flex items-center gap-1">
            <span class="text-ink-600">Per page</span>
            <select
              class="rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-ink-900"
              value={pageSize()}
              onChange={(e) => changePageSize(parseInt(e.currentTarget.value, 10))}
            >
              <For each={opts()}>{(n) => <option value={n}>{n}</option>}</For>
            </select>
          </label>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="rounded border border-ink-300 px-2 py-0.5 text-ink-900 hover:bg-ink-200 disabled:opacity-40"
              disabled={clampedPage() <= 1}
              onClick={() => setPage(clampedPage() - 1)}
            >‹ Prev</button>
            <span class="min-w-[5em] text-center">
              Page {clampedPage()} / {totalPages()}
            </span>
            <button
              type="button"
              class="rounded border border-ink-300 px-2 py-0.5 text-ink-900 hover:bg-ink-200 disabled:opacity-40"
              disabled={clampedPage() >= totalPages()}
              onClick={() => setPage(clampedPage() + 1)}
            >Next ›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
